import React, { JSX, useState } from 'react';
import { Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { sanitize } from '@ibe/services';
import { countries, CountryDataEntry } from 'country-data-list';
import { CircleFlag } from 'react-circle-flags';
import Select, { components, OptionProps, SingleValue } from 'react-select';
import { useFormContext } from '../Form';

export interface CountryOption {
  value: string;
  label: string;
  iso: string;
}

const COUNTRY_OPTIONS: CountryOption[] = countries.all
  .filter(
    (c: CountryDataEntry): c is CountryDataEntry => !!c.alpha2 && !!c.countryCallingCodes?.length
  )
  .map(c => ({
    value: c.countryCallingCodes[0].replace('+', ''),
    label: c.name,
    iso: c.alpha2
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const findByDialCode = (dialCode?: string): CountryOption =>
  COUNTRY_OPTIONS.find(c => c.value === dialCode) ||
  (COUNTRY_OPTIONS.find(c => c.iso === 'DE') as CountryOption);

export const stripLeadingZero = (value: string): string => (value || '').replace(/^0+/, ''); // NOSONAR: replaceAll not available at current TS lib target

const filterCountryOption = (option: { data: CountryOption }, rawInput: string): boolean => {
  const input = rawInput.toLowerCase();
  return option.data.label.toLowerCase().includes(input) || option.data.value.includes(input);
};

const formatOptionLabel = (
  option: CountryOption,
  meta: { context: 'menu' | 'value' }
): JSX.Element => (
  <div className="phone-country-select__option-content">
    <CircleFlag countryCode={option.iso.toLowerCase()} className="phone-number-input__flag" />
    {meta.context === 'menu' && (
      <span className="phone-country-select__option-name">{option.label}</span>
    )}
    <span className="phone-number-input__dial">+{option.value}</span>
  </div>
);

const OptionComponent = (props: OptionProps<CountryOption, false>) => (
  <components.Option {...props} />
);

interface Props {
  id?: string;
  name: string;
  prefixName: string;
  ariaLabel?: string;
  label?: string;
  boldLabel?: boolean;
  showErrorIcon?: boolean;
  disabled?: boolean;
  helperText?: string;
  validateOnBlurOnly?: boolean;
  showSuccessState?: boolean;
  defaultCountry: string;
  noResultsText?: string;
}

const FormPhoneInput = (props: Props): JSX.Element | null => {
  const {
    id,
    name,
    prefixName,
    ariaLabel,
    label,
    boldLabel,
    showErrorIcon,
    disabled,
    helperText,
    validateOnBlurOnly,
    showSuccessState,
    defaultCountry,
    noResultsText
  } = props;

  const { register, errors, control, formState, trigger } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};
  const [isFocused, setIsFocused] = useState(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);

  const fieldName = name;

  if (!register) return null;

  const hasError = !!errors?.[fieldName] || !!errors?.[prefixName];
  const isTouched = !!touchedFields?.[fieldName];
  const fieldValue = _formValues?.[fieldName];
  const hasValue = typeof fieldValue === 'string' ? fieldValue.length > 0 : !!fieldValue;

  const displayError = validateOnBlurOnly
    ? showErrorIcon !== undefined &&
      hasError &&
      (isSubmitted || isTouched || hasBeenBlurred) &&
      !isFocused
    : hasError;

  const displaySuccess = showSuccessState ? !hasError && hasValue && !isFocused : false;

  const idPrefix = id ? `${id}-${name}` : name;
  const elementId = `form-builder-phoneinput-${idPrefix}`;

  return (
    <div
      className={classNames('form-input', 'phone-number-input', 'form-input-' + name, {
        'input--error': displayError,
        'form-input--errors': displayError,
        'form-input--touched': isTouched,
        'form-input--dirty': dirtyFields?.[fieldName],
        'form-input--form-submitted': isSubmitted,
        'form-input--success': displaySuccess
      })}
    >
      {!!label && (
        <Label
          className={classNames('form-label', {
            form__error__top: displayError,
            'font-weight-bold': boldLabel
          })}
          htmlFor={elementId}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}

      <Controller
        name={prefixName}
        control={control}
        defaultValue={
          (COUNTRY_OPTIONS.find(c => c.iso === defaultCountry) || findByDialCode()).value
        }
        render={({ field: prefixField }) => (
          <Controller
            name={fieldName}
            control={control}
            render={({ field: numberField }) => {
              const selectedOption = findByDialCode(prefixField.value);

              return (
                <div className="phone-number-input__row">
                  <Select<CountryOption, false>
                    inputId={`${elementId}-country-select`}
                    classNamePrefix="phone-country-select"
                    className="phone-country-select"
                    options={COUNTRY_OPTIONS}
                    value={selectedOption}
                    isDisabled={disabled}
                    isSearchable
                    filterOption={filterCountryOption}
                    formatOptionLabel={formatOptionLabel}
                    placeholder=""
                    noOptionsMessage={() => noResultsText}
                    aria-label={ariaLabel ?? label ?? ''}
                    components={{
                      Option: OptionComponent,
                      IndicatorSeparator: () => null
                    }}
                    onChange={(option: SingleValue<CountryOption>) => {
                      if (option) prefixField.onChange(option.value);
                    }}
                  />

                  <input
                    id={elementId}
                    data-testid={'form-input-' + name}
                    ref={numberField.ref}
                    className="phone-number-input__number"
                    type="tel"
                    inputMode="tel"
                    aria-label={ariaLabel ?? label ?? ''}
                    disabled={disabled}
                    value={numberField.value ?? ''}
                    onFocus={() => setIsFocused(true)}
                    onBlur={async e => {
                      setIsFocused(false);
                      setHasBeenBlurred(true);
                      numberField.onChange(stripLeadingZero(e.target.value));
                      numberField.onBlur();
                      if (trigger) await trigger([fieldName, prefixName]);
                    }}
                    onChange={e => {
                      const raw = e.target.value.replace(/[^\d\s/-]/g, ''); // NOSONAR: replaceAll not available at current TS lib target
                      numberField.onChange(raw);
                    }}
                  />
                </div>
              );
            }}
          />
        )}
      />
      {!displayError && helperText && <div className="form__helper-text">{helperText}</div>}
      {displayError && (
        <div className="form__error">
          {(errors?.[fieldName]?.message as string) || (errors?.[prefixName]?.message as string)}
        </div>
      )}
      {showErrorIcon && displayError && (
        <FontAwesomeIcon icon={faExclamationTriangle} className="form-input__error-icon" />
      )}
      {displaySuccess && <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />}
    </div>
  );
};

export default FormPhoneInput;
